import { Select } from '@/components/ui/select';
import { getProfileDashboardPath, getSwitchableGymProfiles } from '@/lib/authRouting';
import { useAuthStore } from '@/stores/authStore';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * GymSwitcher Component
 *
 * Shown in TopBar when the active profile is `affiliate_member` and the user
 * belongs to more than one gym. Allows switching gym context by switching to
 * the matching affiliate_member profile.
 *
 * Hidden when user has only one gym (or is not a member).
 */
export function GymSwitcher() {
  const { profiles, activeProfile, switchProfile } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);

  // Only relevant when the active profile is affiliate_member
  if (activeProfile?.profile_type !== 'affiliate_member') {
    return null;
  }

  // Gym switching stays within the active portal role. The role toggle handles
  // switching between owner, coach, and member contexts separately.
  const gymProfiles = getSwitchableGymProfiles(profiles || [], activeProfile);

  // Hide when only one gym
  if (gymProfiles.length <= 1) {
    return null;
  }

  const handleSwitch = async (profileId: string) => {
    if (profileId === activeProfile?.id) return;

    const targetProfile = gymProfiles.find((p) => p.id === profileId);
    if (!targetProfile) return;

    setIsLoading(true);
    try {
      await switchProfile(profileId);
      // Stay on the current portal page when possible so members/programming
      // reload under the new gym. Leave only when the portal changes (e.g.
      // owner → member).
      const dashboardPath = getProfileDashboardPath(targetProfile);
      const onSamePortal =
        location.pathname === dashboardPath || location.pathname.startsWith(`${dashboardPath}/`);
      if (!onSamePortal) {
        navigate(dashboardPath);
      }
      toast.success('Switched gym');
    } catch {
      toast.error('Failed to switch gym');
    } finally {
      setIsLoading(false);
    }
  };

  const options = gymProfiles.map((p) => ({
    value: p.id,
    label: p.context?.affiliate?.name || p.affiliate_id || 'Gym',
  }));

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm text-muted-foreground">Switching...</span>
      </div>
    );
  }

  return (
    <Select
      options={options}
      value={activeProfile?.id || ''}
      onChange={(e) => handleSwitch(e.target.value)}
      className="min-w-[150px] max-w-[200px] h-10 text-sm"
    />
  );
}
